import {
  booleanAttribute,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  Optional,
  Output,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  MatFormField,
  MatFormFieldControl,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerActions,
  MatDatepickerInput,
} from '@angular/material/datepicker';
import {
  ControlValueAccessor,
  NgControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CommonService } from '../../services/common.service';
import { IDropdownResponse } from '../../models/dynamic.model';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { getOffsetFromZoneName, toIanaZone } from '../../utility/date-format';
import { dateTimeFormat } from '../../utility/constants';
import { DateTime } from 'luxon';
import { map, Observable, startWith, Subject } from 'rxjs';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { AsyncPipe } from '@angular/common';
import { FieldClearSuffixComponent } from '../field-clear-suffix/field-clear-suffix.component';

interface TimeValue {
  hour: number;
  minute: number;
  second: number;
  amPm: 'AM' | 'PM';
  timeZone: string;
  offset: string;
  date: Date;
  timeZoneId?: number;
  formattedString?: string;
  dateString?: string;
}

export interface DateTimePickerValue {
  dateTime: string;
  timeZoneId: number;
}

/** The typeable part of the displayed value (`allowTyping`). */
const TYPED_DATE_TIME_FORMAT = 'MM/dd/yyyy HH:mm:ss';

/**
 * @description This component provides a date and time picker with timezone selection functionality.
 * It implements both ControlValueAccessor and MatFormFieldControl interfaces, making it compatible with Angular
 * reactive forms and Material form fields. The component supports time input with AM/PM format and timezone selection.
 *
 * @example
 * Template for reactive forms usage:
 * ```html
 * <form [formGroup]="exampleForm">
 *   <mat-form-field>
 *     <mat-label>Event Date & Time</mat-label>
 *     <app-date-time-picker
 *       [timeZones]="availableTimeZones"
 *       placeholder="Select event date and time"
 *       formControlName="eventDateTime"
 *       [required]="true"
 *       (valueChange)="onEventDateTimeChange($event)">
 *     </app-date-time-picker>
 *     <mat-error *ngIf="exampleForm.get('eventDateTime')?.hasError('required')">
 *       Event date and time is required
 *     </mat-error>
 *   </mat-form-field>
 * </form>
 * ```
 * @example
 * // Usage with reactive forms
 * ```typescript
 * import { Component, OnInit } from '@angular/core';
 * import { FormBuilder, FormGroup, Validators } from '@angular/forms';
 * import { IDropdownResponse } from './models/dynamic.model';
 * import { DateTimePickerValue, TimeValue } from './date-time-picker.component';
 *
 * @Component({
 *   selector: 'app-example',
 *   template: `<added-separately>`
 * })
 * export class ExampleComponent implements OnInit {
 *   exampleForm: FormGroup;
 *   availableTimeZones: IDropdownResponse[] = [
 *     { id: 1, name: 'Eastern Time', timeZoneCode: 'America/New_York' },
 *     { id: 2, name: 'Central Time', timeZoneCode: 'America/Chicago' },
 *     { id: 3, name: 'Pacific Time', timeZoneCode: 'America/Los_Angeles' }
 *   ];
 *
 *   constructor(private fb: FormBuilder) {}
 *
 *   ngOnInit() {
 *     this.exampleForm = this.fb.group({
 *       eventDateTime: [null, Validators.required]
 *     });
 *   }
 *
 *   onEventDateTimeChange(timeValue: TimeValue) {
 *     console.log('Selected time:', timeValue);
 *     console.log('Formatted string:', timeValue.formattedString);
 *     console.log('UTC date string:', timeValue.dateString);
 *   }
 * }
 * ```
 *
 * @example
 * // Setting initial value programmatically
 * ```typescript
 * // Set initial value
 * const initialValue: DateTimePickerValue = {
 *   dateTime: '2024-05-06T14:30:00.000Z', // ISO string
 *   timeZoneId: 1 // ID from timeZones array
 * };
 *
 * this.exampleForm.patchValue({
 *   eventDateTime: initialValue
 * });
 * ```
 *
 * @example
 * // Standalone usage without reactive forms - Component:
 * ```html
 * <app-date-time-picker
 *       [timeZones]="timeZoneList"
 *       placeholder="Pick a time"
 *       [value]="selectedDateTime"
 *       (valueChange)="handleTimeChange($event)"
 *       (closed)="onPickerClosed()">
 * </app-date-time-picker>
 * ```
 *
 * @example
 * // Standalone usage without reactive forms
 * ```typescript
 * @Component({
 * @example
 *   template: ``
 * })
 * export class StandaloneExampleComponent {
 *   selectedDateTime: DateTimePickerValue | null = null;
 *   timeZoneList: IDropdownResponse[] = [...];
 *
 *   handleTimeChange(timeValue: TimeValue) {
 *     this.selectedDateTime = {
 *       dateTime: timeValue.dateString!,
 *       timeZoneId: timeValue.timeZoneId!
 *     };
 *   }
 *
 *   onPickerClosed() {
 *     console.log('Date time picker closed');
 *   }
 * }
 * ```
 *
 * @inputs
 * @param {IDropdownResponse[]} timeZones - Array of timezone options with id, name, and timeZoneCode
 * @param {string} placeholder - Placeholder text for the input field
 * @param {DateTimePickerValue | null} value - Current value of the date time picker
 * @param {boolean} required - Whether the field is required (default: false)
 *
 * @outputs
 * @param {EventEmitter<TimeValue>} valueChange - Emits when the date/time value changes
 * @param {EventEmitter<void>} closed - Emits when the date picker is closed
 *
 *
 * @interface DateTimePickerValue
 * @property {string} dateTime - ISO date string in UTC
 * @property {number} timeZoneId - ID of the selected timezone
 *
 * @notes
 * - The component uses 12-hour format with AM/PM selection
 * - Time values are validated within their respective ranges (0-12 for hours, 0-59 for minutes/seconds)
 * - The component automatically converts times to UTC for consistent storage
 * - Timezone selection affects the display and interpretation of the selected time
 * - The component is fully compatible with Angular reactive forms and template-driven forms
 *
 */
@Component({
  selector: 'app-date-time-picker',
  standalone: true,
  imports: [
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatSuffix,
    MatOption,
    ReactiveFormsModule,
    MatButton,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatDatepicker,
    MatDatepickerActions,
    MatDatepickerInput,
    AsyncPipe,
    FieldClearSuffixComponent,
  ],
  templateUrl: './date-time-picker.component.html',
  styleUrl: './date-time-picker.component.scss',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: DateTimePickerComponent,
    },
  ],
})
export class DateTimePickerComponent
  implements ControlValueAccessor, MatFormFieldControl<DateTimePickerValue>, OnChanges
{
  static nextId = 0;
  @HostBinding() id = `date-time-picker-${DateTimePickerComponent.nextId++}`;
  @HostBinding('class.allow-typing') get typingClass(): boolean {
    return this.allowTyping;
  }

  @ViewChild(MatDatepicker, { static: true })
  datePicker!: MatDatepicker<Date>;
  @ViewChild('input', { read: ElementRef, static: true })
  input!: ElementRef<HTMLInputElement>;

  disabled = false;
  readonly minHours = 0;
  readonly maxHours = 12;
  readonly minMinutes = 0;
  readonly maxMinutes = 59;
  readonly minSeconds = 0;
  readonly maxSeconds = 59;
  private _required = false;
  formBuilder = inject(NonNullableFormBuilder);
  commonService = inject(CommonService);
  onChange?: (val: DateTimePickerValue | null) => void;
  onTouched?: () => void;
  stateChanges = new Subject<void>();
  formattedString?: string;
  private lastValue: DateTimePickerValue | null = null;

  form = this.formBuilder.group({
    hour: ['00', Validators.required],
    minute: ['00', Validators.required],
    second: ['00', Validators.required],
    amPm: this.formBuilder.control<TimeValue['amPm']>(
      'AM',
      Validators.required
    ),
    timeZone: this.formBuilder.control<string>('', Validators.required),
    timeZoneId: this.formBuilder.control<number>(0, Validators.required),
  });

  @Input() timeZones: IDropdownResponse[] = [];
  @Input() placeholder!: string;
  /**
   * Opt-in: the value can also be typed as `MM/dd/yyyy HH:mm:ss` (read in the
   * selected time zone). A click on the input no longer opens the calendar, so
   * the consumer adds a `matSuffix` toggle that calls `openPicker()`.
   * An unparseable value sets `invalidDateTime`.
   */
  @Input({ transform: booleanAttribute }) allowTyping = false;
  /** Opt-in: the time zone is shown in the panel but can't be changed. */
  @Input({ transform: booleanAttribute }) lockTimeZone = false;
  private _showTimeZone = true;
  private inputFocused = false;
  private hasPickedValue = false;
  private typedValueInvalid = false;
  private readonly typedValueValidator: ValidatorFn = () =>
    this.typedValueInvalid ? { invalidDateTime: true } : null;

  @Input()
  get showTimeZone(): boolean {
    return this._showTimeZone;
  }

  set showTimeZone(value: BooleanInput) {
    this._showTimeZone = coerceBooleanProperty(value);
    this.updateTimeZoneValidators();
  }

  @Input()
  set value(val: DateTimePickerValue | null) {
    if (!val?.dateTime) {
      this.resetPickerValue();
      return;
    }

    const timeZone =
      this.timeZones.find(zone => zone.id === val.timeZoneId) ??
      this.getDefaultTimeZone();
    if (!timeZone) {
      this.formattedString = '';
      return;
    }

    const dateTimeInstance = DateTime.fromISO(val.dateTime).setZone(
      toIanaZone(timeZone.name)
    );

    this.patchPicker(dateTimeInstance, timeZone);
    this.hasPickedValue = true;
    this.formattedString = this.getFormattedDateString(
      dateTimeInstance,
      timeZone
    );
  }

  filteredTimeZones$?: Observable<IDropdownResponse[]> = this.form
    .get('timeZone')
    ?.valueChanges.pipe(
      startWith(''),
      map(str => {
        return this.timeZones.filter(
          zone =>
            zone.name.toLowerCase().includes(str.toLowerCase()) ||
            zone.timeZoneCode?.toLowerCase().includes(str.toLowerCase())
        );
      })
    );

  @Output() valueChange = new EventEmitter<TimeValue>();
  @Output() closed = new EventEmitter<void>();

  constructor(@Optional() @Self() public ngControl: NgControl) {
    // Replace the provider from above with this.
    if (this.ngControl) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
    this.updateTimeZoneValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['timeZones'] && this.lastValue) {
      this.applyStoredValue();
    }
  }

  get focused(): boolean {
    return this.datePicker.opened || this.inputFocused;
  }

  get empty(): boolean {
    return !this.formattedString;
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(req: BooleanInput) {
    this._required = coerceBooleanProperty(req) as boolean;
    this.stateChanges.next();
  }

  get errorState(): boolean {
    return this.form.invalid && this.form.touched;
  }

  setDescribedByIds(ids: string[]) {
    // @TODO: handle this once we implement aria-labels in our app
  }

  onContainerClick(event: MouseEvent) {
    if (this.disabled) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.closest('app-field-clear-suffix, .mat-mdc-form-field-icon-suffix')) {
      return;
    }
    if (this.allowTyping) {
      this.input?.nativeElement.focus();
      return;
    }
    this.openPicker(event);
  }

  openPicker(event?: Event): void {
    event?.stopPropagation();
    if (this.disabled || this.datePicker?.opened) {
      return;
    }
    this.input?.nativeElement.focus();
    this.onTouched?.();
    this.datePicker.open();
  }

  writeValue(obj: DateTimePickerValue | null): void {
    this.lastValue = obj;
    this.typedValueInvalid = false;
    this.applyStoredValue();
  }

  /** `allowTyping`: parse on every keystroke so the form's validity follows the text. */
  onTypedInput(text: string): void {
    if (!this.allowTyping) {
      return;
    }
    this.formattedString = text;
    this.addTypedValueValidator();
    const trimmed = text.trim();
    if (!trimmed) {
      this.typedValueInvalid = false;
      this.onChange?.(null);
      this.stateChanges.next();
      return;
    }
    const timeZone = this.getTypingTimeZone();
    const parsed = timeZone
      ? DateTime.fromFormat(trimmed, TYPED_DATE_TIME_FORMAT, {
          zone: toIanaZone(timeZone.name),
        })
      : null;
    if (!timeZone || !parsed?.isValid) {
      this.typedValueInvalid = true;
      this.onChange?.(null);
      this.stateChanges.next();
      return;
    }
    this.typedValueInvalid = false;
    this.patchPicker(parsed, timeZone);
    this.hasPickedValue = true;
    this.onChange?.({
      dateTime: parsed.toUTC().toString(),
      timeZoneId: timeZone.id,
    });
    this.stateChanges.next();
  }

  /** `allowTyping`: drop the zone suffix so only the typeable part is edited. */
  onTypedFocus(): void {
    if (!this.allowTyping) {
      return;
    }
    this.inputFocused = true;
    if (this.formattedString && !this.typedValueInvalid) {
      this.formattedString = this.formattedString.slice(
        0,
        TYPED_DATE_TIME_FORMAT.length
      );
    }
    this.stateChanges.next();
  }

  /** `allowTyping`: put the zone suffix back once the typed value is valid. */
  onTypedBlur(): void {
    if (!this.allowTyping) {
      return;
    }
    this.inputFocused = false;
    this.onTouched?.();
    const text = (this.formattedString ?? '').trim();
    const timeZone = this.getTypingTimeZone();
    if (text && !this.typedValueInvalid && timeZone) {
      const parsed = DateTime.fromFormat(text, TYPED_DATE_TIME_FORMAT, {
        zone: toIanaZone(timeZone.name),
      });
      if (parsed.isValid) {
        this.formattedString = this.getFormattedDateString(parsed, timeZone);
      }
    }
    this.stateChanges.next();
  }

  private addTypedValueValidator(): void {
    const control = this.ngControl?.control;
    if (control && !control.hasValidator(this.typedValueValidator)) {
      control.addValidators(this.typedValueValidator);
    }
  }

  /** The zone already on the picker, else the user's default (as when the panel opens). */
  private getTypingTimeZone(): IDropdownResponse | undefined {
    if (this.hasPickedValue) {
      const { timeZone, timeZoneId } = this.form.getRawValue();
      const selectedZone = this.resolveSelectedZone(timeZone, timeZoneId);
      if (selectedZone) {
        return selectedZone;
      }
    }
    return this.getUserDefaultTimeZone();
  }

  private applyStoredValue(): void {
    this.value = this.lastValue;
  }

  registerOnChange(fn: (val: DateTimePickerValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
    this.stateChanges.next();
  }

  // Hour manipulation
  incrementHours(): void {
    if (this.disabled) return;
    const hoursControl = this.form.get('hour');
    let hours = hoursControl?.value ?? 0;
    hours = +hours;

    if (hours < this.maxHours) {
      hours++;
    } else {
      hours = this.minHours;
    }

    hoursControl?.setValue(this.formatNumber(hours));
    this.onTouched?.();
    this.stateChanges.next();
  }

  decrementHours(): void {
    if (this.disabled) return;
    const hoursControl = this.form.get('hour');
    let hours = hoursControl?.value ?? 0;
    hours = +hours;

    if (hours > this.minHours) {
      hours--;
    } else {
      hours = this.maxHours;
    }

    hoursControl?.setValue(this.formatNumber(hours));
    this.onTouched?.();
    this.stateChanges.next();
  }

  onHoursInputChange(event: Event): void {
    if (this.disabled) return;

    const target = event.target as HTMLInputElement;
    let value = parseInt(target.value, 10);

    if (isNaN(value)) {
      value = 0;
    } else if (value < this.minHours) {
      value = this.minHours;
    } else if (value > this.maxHours) {
      value = this.maxHours;
    }

    target.value = this.formatNumber(value);
    this.form.get('hour')?.setValue(this.formatNumber(value));
    this.onTouched?.();
    this.stateChanges.next();
  }

  // Minutes manipulation
  incrementMinutes(): void {
    if (this.disabled) return;

    const minutesControl = this.form.get('minute');
    let minutes = minutesControl?.value ?? 0;
    minutes = +minutes;

    if (minutes < this.maxMinutes) {
      minutes++;
    } else {
      minutes = this.minMinutes;
    }
    minutesControl?.setValue(this.formatNumber(minutes));
    this.onTouched?.();
    this.stateChanges.next();
  }

  decrementMinutes(): void {
    if (this.disabled) return;
    const minutesControl = this.form.get('minute');
    let minutes = minutesControl?.value ?? 0;
    minutes = +minutes;

    if (minutes > this.minMinutes) {
      minutes--;
    } else {
      minutes = this.maxMinutes;
    }
    minutesControl?.setValue(this.formatNumber(minutes));
    this.onTouched?.();
    this.stateChanges.next();
  }

  // Minutes manipulation
  incrementSeconds(): void {
    if (this.disabled) return;
    const secondsControl = this.form.get('second');
    let seconds = secondsControl?.value ?? 0;
    seconds = +seconds;

    if (seconds < this.maxSeconds) {
      seconds++;
    } else {
      seconds = this.minSeconds;
    }
    secondsControl?.setValue(this.formatNumber(seconds));
    this.onTouched?.();
    this.stateChanges.next();
  }

  decrementSeconds(): void {
    if (this.disabled) return;
    const secondsControl = this.form.get('second');
    let seconds = secondsControl?.value ?? 0;
    seconds = +seconds;

    if (seconds > this.minSeconds) {
      seconds--;
    } else {
      seconds = this.maxSeconds;
    }
    secondsControl?.setValue(this.formatNumber(seconds));
    this.onTouched?.();
    this.stateChanges.next();
  }

  onMinutesInputChange(event: Event): void {
    if (this.disabled) return;

    const target = event.target as HTMLInputElement;
    let value = parseInt(target.value, 10);

    if (isNaN(value)) {
      value = 0;
    } else if (value < this.minMinutes) {
      value = this.minMinutes;
    } else if (value > this.maxMinutes) {
      value = this.maxMinutes;
    }

    target.value = this.formatNumber(value);
    this.form.get('minute')?.setValue(this.formatNumber(value));
    this.onTouched?.();
    this.stateChanges.next();
  }

  onSecondsInputChange(event: Event): void {
    if (this.disabled) return;

    const target = event.target as HTMLInputElement;
    let value = parseInt(target.value, 10);

    if (isNaN(value)) {
      value = 0;
    } else if (value < this.minSeconds) {
      value = this.minSeconds;
    } else if (value > this.maxSeconds) {
      value = this.maxSeconds;
    }

    target.value = this.formatNumber(value);
    this.form.get('second')?.setValue(this.formatNumber(value));
    this.onTouched?.();
    this.stateChanges.next();
  }

  updateAMPM() {
    const amPmControl = this.form.get('amPm');
    const currentValue = amPmControl?.value;
    if (currentValue === 'AM') {
      amPmControl?.setValue('PM');
    } else {
      amPmControl?.setValue('AM');
    }
    this.onTouched?.();
    this.stateChanges.next();
  }

  // Utility methods
  formatNumber(value: number): string {
    return value.toString().padStart(2, '0');
  }

  onSubmit() {
    const value = this.form.getRawValue();
    const selected =
      this.datePicker?.['_componentRef']?.instance._calendar.selected ??
      new Date();
    const selectedZone = this.resolveSelectedZone(
      value.timeZone,
      value.timeZoneId
    );
    if (!selectedZone) {
      return;
    }

    this.form.patchValue({
      timeZone: selectedZone.name,
      timeZoneId: selectedZone.id,
    });
    this.form.get('timeZone')?.setErrors(null);

    const selectedDate = selected as Date;
    const parsedDate = DateTime.fromObject(
      {
        year: selectedDate.getFullYear(),
        month: selectedDate.getMonth() + 1,
        day: selectedDate.getDate(),
        hour: this.to24Hour(+value.hour, value.amPm),
        minute: +value.minute,
        second: +value.second,
      },
      { zone: toIanaZone(selectedZone.name) }
    );

    const patchedValue: TimeValue = {
      hour: +value.hour,
      second: +value.second,
      minute: +value.minute,
      amPm: value.amPm,
      timeZone: selectedZone.name,
      offset: getOffsetFromZoneName(selectedZone.name),
      date: selectedDate,
      formattedString: this.getFormattedDateString(parsedDate, selectedZone),
      timeZoneId: selectedZone.id,
      dateString: parsedDate.toUTC().toString(),
    };
    this.valueChange.emit(patchedValue);
    this.typedValueInvalid = false;
    this.hasPickedValue = true;
    this.onChange?.({
      dateTime: parsedDate.toUTC().toString(),
      timeZoneId: selectedZone.id,
    });
    this.formattedString = patchedValue.formattedString;
    this.datePicker?.select(selectedDate);
    this.datePicker?.close();
    this.stateChanges.next();
  }

  onPickerOpened(): void {
    if (this.formattedString || !this.timeZones.length) {
      return;
    }
    this.initializePickerDefaults();
  }

  onTimeZoneSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedZone = this.timeZones.find(
      zone => zone.name === event.option.value
    );
    if (!selectedZone) {
      return;
    }
    this.form.patchValue({
      timeZone: selectedZone.name,
      timeZoneId: selectedZone.id,
    });
    this.form.get('timeZone')?.setErrors(null);
    this.onTouched?.();
    this.stateChanges.next();
  }

  getFormattedDateString(date: DateTime, timeZone: IDropdownResponse) {
    if (!this.showTimeZone) {
      return date.toFormat(dateTimeFormat.statusCodeDateTime);
    }
    const zoneLabel = timeZone.timeZoneCode || date.offsetNameShort || 'UTC';
    return `${date.toFormat(dateTimeFormat.statusCodeDateTime)} ${zoneLabel} {${date.toUTC().toFormat('HH:mm')} ${date.toFormat('ZZ')}}`;
  }

  formAutocompleteValueChanges() {
    const timeZoneValue = this.form.get('timeZone')?.value;
    if (!timeZoneValue) {
      this.form.patchValue({ timeZoneId: 0 }, { emitEvent: false });
      return;
    }
    setTimeout(() => {
      this.commonService.getAutocompleteDropdownId(
        this.timeZones,
        this.form,
        'timeZone',
        'timeZoneId',
        false
      );
    }, 0);
  }

  private initializePickerDefaults(): void {
    const defaultZone = this.getUserDefaultTimeZone();
    if (!defaultZone) {
      return;
    }

    const now = DateTime.now().setZone(toIanaZone(defaultZone.name));
    this.patchPicker(now, defaultZone);
    this.form.get('timeZone')?.setErrors(null);
  }

  /** Calendar date + time fields + zone of the panel, from one instant. */
  private patchPicker(date: DateTime, timeZone: IDropdownResponse): void {
    this.datePicker?.select(this.toCalendarDate(date));
    this.form.patchValue({
      hour: date.toFormat('hh'),
      minute: date.toFormat('mm'),
      second: date.toFormat('ss'),
      amPm: date.toFormat('a') === 'AM' ? 'AM' : 'PM',
      timeZone: timeZone.name,
      timeZoneId: timeZone.id,
    });
  }

  private getUserDefaultTimeZone(): IDropdownResponse | undefined {
    const storedTimezoneId = +(localStorage.getItem('timezoneId') ?? '0');
    return (
      this.timeZones.find(zone => zone.id === storedTimezoneId) ??
      this.getDefaultTimeZone()
    );
  }

  private resolveSelectedZone(
    timeZoneName: string,
    timeZoneId: number
  ): IDropdownResponse | undefined {
    if (timeZoneId) {
      const zoneById = this.timeZones.find(zone => zone.id === timeZoneId);
      if (zoneById) {
        return zoneById;
      }
    }
    if (timeZoneName) {
      const exactMatch = this.timeZones.find(
        zone => zone.name === timeZoneName
      );
      if (exactMatch) {
        return exactMatch;
      }
      const partialMatch = this.timeZones.find(zone =>
        zone.name.toLowerCase().includes(timeZoneName.toLowerCase())
      );
      if (partialMatch) {
        return partialMatch;
      }
    }
    return this.showTimeZone ? undefined : this.getDefaultTimeZone();
  }

  private toCalendarDate(date: DateTime): Date {
    return new Date(date.year, date.month - 1, date.day);
  }

  private to24Hour(hour: number, amPm: TimeValue['amPm']): number {
    if (amPm === 'AM') {
      return hour === 12 ? 0 : hour;
    }
    return hour === 12 ? 12 : hour + 12;
  }

  private getDefaultTimeZone(): IDropdownResponse | undefined {
    return (
      this.timeZones.find(zone => zone.timeZoneCode === 'UTC') ??
      this.timeZones[0]
    );
  }

  private resetPickerValue(): void {
    this.hasPickedValue = false;
    this.formattedString = '';
    this.form.reset({
      hour: '00',
      minute: '00',
      second: '00',
      amPm: 'AM',
      timeZone: '',
      timeZoneId: 0,
    });
    const defaultTimeZone = this.getDefaultTimeZone();
    if (defaultTimeZone) {
      this.form.patchValue({
        timeZone: defaultTimeZone.name,
        timeZoneId: defaultTimeZone.id,
      });
    }
    this.form.get('timeZone')?.setErrors(null);
    this.stateChanges.next();
  }

  private updateTimeZoneValidators(): void {
    const timeZoneControl = this.form.get('timeZone');
    const timeZoneIdControl = this.form.get('timeZoneId');
    if (this._showTimeZone) {
      timeZoneControl?.setValidators(Validators.required);
      timeZoneIdControl?.setValidators([Validators.required, Validators.min(1)]);
    } else {
      timeZoneControl?.clearValidators();
      timeZoneIdControl?.clearValidators();
      const defaultTimeZone = this.getDefaultTimeZone();
      if (defaultTimeZone) {
        this.form.patchValue({
          timeZone: defaultTimeZone.name,
          timeZoneId: defaultTimeZone.id,
        });
      }
    }
    timeZoneControl?.updateValueAndValidity({ emitEvent: false });
    timeZoneIdControl?.updateValueAndValidity({ emitEvent: false });
  }
}
